import { defineDocument, type LineGridDef } from './types.js'
import { narrowed, stillOpen } from './lookup-narrowing.js'
import {
  ALL_TABS, BASIC_ACTIONS, FULL_ACTIONS, REVISABLE_TABS, SENDABLE_ACTIONS,
  SYNCED_ACTIONS, SYNCED_BASIC_ACTIONS,
  adjustmentsGrid, baseColumns, baseFilters, chargesGrid, col, f,
  currencySection, fcyColumns, interstate, invoiceStatusColumn, invoiceStatusFilter, jobType, opt,
  pricedLines, sentColumn, sentFilter, taxUnit, taxUnitChip, termsSection, withBillingUnit,
  OURS, ofParty, ofPartyOrOurs, scoped,
} from './common.js'

const YES_NO = [{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }] as const

/**
 * docs/06 §1 — the suppliers invited to price an RFQ.
 *
 * A participant is a line, so inviting one, recording what they said and
 * awarding the work all ride on the chassis every other grid uses. The
 * invitation token the supplier portal reads hangs off this row.
 */
const participantGrid: LineGridDef = {
  section: 'PARTICIPANT',
  // "Supplier List": four summary columns, and each supplier's details in
  // the "Supplier Details" dialog that "Add supplier" opens (PUR-004).
  title: 'Supplier list', n: '05',
  addLabel: 'Add supplier',
  emptyText: 'Add the suppliers participating in this RFQ',
  addOpensDialog: true,
  detailTitle: 'Supplier details',
  totalsAs: 'NONE',
  countAs: { label: 'Participants', noun: 'supplier(s)' },
  /*
    PUR-020 — an existing supplier and one of its contacts, whose email and
    phones are read off the contact card; or a new supplier by company and
    contact, registered as a provisional supplier when the RFQ is saved. The
    dialog asks only what the supplier type needs, and an existing contact's
    email and phones read as the contact card has them.
  */
  columns: [
    col.seq(),
    { key: 'supplierId', label: 'Supplier', type: 'text', editable: true, lookup: { entity: 'supplier' },
      required: true, visibleWhen: { column: 'participantType', equals: ['EXISTING'] }, elseShow: 'companyName' },
    { key: 'participantType', label: 'Supplier type', type: 'text', width: 170, editable: true, required: true,
      control: 'radio',
      options: [{ value: 'EXISTING', label: 'Existing supplier' }, { value: 'NEW', label: 'New supplier' }],
      hint: 'A new supplier is put on the books as provisional when this is saved.' },
    { key: 'companyName', label: 'Company name', type: 'text', width: 200, editable: true, dialogOnly: true,
      required: true, visibleWhen: { column: 'participantType', equals: ['NEW'] } },
    { key: 'contactId', label: 'Supplier contact', type: 'text', width: 200, editable: true, dialogOnly: true,
      required: true, visibleWhen: { column: 'participantType', equals: ['EXISTING'] },
      lookup: { entity: 'contact', scope: ofParty('supplierId'), refill: true,
        fills: { email: 'contactEmail', mobile: 'contactMobile', landline: 'contactLandline' } },
      hint: 'One of the supplier’s contacts. Their email and phones are read from the contact.' },
    { key: 'contactName', label: 'Contact name', type: 'text', width: 170, editable: true, dialogOnly: true,
      required: true, visibleWhen: { column: 'participantType', equals: ['NEW'] } },
    // Where the invitation goes. Read off the contact card for an existing
    // supplier; typed for a new one.
    { key: 'contactEmail', label: 'Contact email', type: 'text', width: 220, editable: true, dialogOnly: true,
      visibleWhen: { column: 'participantType', isSet: true },
      lockedWhen: { column: 'participantType', equals: ['EXISTING'] } },
    { key: 'contactMobile', label: 'Contact mobile', type: 'text', width: 150, editable: true, dialogOnly: true,
      visibleWhen: { column: 'participantType', isSet: true },
      lockedWhen: { column: 'participantType', equals: ['EXISTING'] } },
    { key: 'contactLandline', label: 'Contact landline', type: 'text', width: 150, editable: true, dialogOnly: true,
      visibleWhen: { column: 'participantType', isSet: true },
      lockedWhen: { column: 'participantType', equals: ['EXISTING'] } },
    { key: 'inviteState', label: 'Invitation', type: 'text', width: 130, dialogOnly: true },
    { key: 'responseState', label: 'Status', type: 'text', width: 200 },
    { key: 'decision', label: 'Decision', type: 'text', width: 150, editable: true,
      options: [
        { value: '', label: 'Not decided' },
        { value: 'AWARDED', label: 'Awarded' },
        { value: 'REJECTED', label: 'Rejected' },
      ] },
    { key: 'quotedTotal', label: 'Quoted', type: 'money', width: 150, align: 'right' },
  ],
  sources: [],
}

/** How a requisitioned material is to be bought (docs/24 §3.4). */
export const PROCUREMENT_ROUTES = [
  ['RFQ', 'Ask for quotations'],
  ['DIRECT', 'Order directly'],
  ['RATE_CONTRACT', 'Against a rate contract'],
  ['LOCAL', 'Buy locally'],
] as const satisfies ReadonlyArray<[string, string]>

/**
 * docs/24 §3.4 — the purchase executive's decision about what the site asked for.
 *
 * "The material request will convert into purchase requisition by the
 * purchase executive." The request says what the site wants; the requisition
 * says what purchasing will actually buy, how, and by when — having looked at
 * the stock on the shelf, the class of each material and how long it takes to
 * arrive. Several sites' requests can be bought together, which is why a
 * requisition may span projects: each line says which job it is for.
 *
 * It consumes the request lines it takes up, and is consumed in turn by the
 * orders raised from it. The enquiry it goes out on in between spends nothing.
 */
export const purchaseRequisition = defineDocument({
  key: 'purchase_requisition',
  group: 'Purchase and Expense',
  blurb: 'Turn approved material requests into what purchasing will buy — how much, how, and by when.',
  route: 'pur_reqs',
  label: 'Purchase requisition',
  labelPlural: 'Purchase requisitions',
  numbering: 'PURCHASE_REQUISITION',
  numberPrefix: 'PR/',
  workflow: 'PURCHASE_REQUISITION',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a purchase requisition',
    subtitle: 'For one project, or several sites’ requests bought together.',
    fields: [
      f.radio('requisitionFor', 'What is this for?', [
        ['PROJECT', 'One project'],
        ['CONSOLIDATED', 'Several projects bought together'],
      ]),
      { ...f.look('projectId', 'Project', 'project', true, 'full'),
        visibleWhen: { field: 'requisitionFor', equals: ['PROJECT'] } },
      f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        f.choice('requisitionFor', 'For', [['PROJECT', 'One project'], ['CONSOLIDATED', 'Several projects']], false),
        { ...f.look('projectId', 'Project', 'project'),
          visibleWhen: { field: 'requisitionFor', equals: ['PROJECT'] } },
        { ...f.look('materialRequestId', 'Against material request', 'material_request'),
          hint: 'Name one request to pull its open lines. Or pick from every open request below.' },
        f.date('documentDate', 'Requisition date', true),
        { ...f.date('requiredDate', 'Needed on site by', true),
          hint: 'The default for every line. A line can say otherwise.' },
        f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true),
        f.choice('priority', 'Priority', [['NORMAL', 'Normal'], ['URGENT', 'Urgent']], false),
        { ...f.choice('procurementRoute', 'Usually bought through', PROCUREMENT_ROUTES, false),
          hint: 'The default for every line. Class C materials are usually bought locally.' },
        { ...f.look('warehouseId', 'Deliver to warehouse', 'warehouse'),
          hint: 'Left empty the materials go to the project site.' },
        jobType(),
        taxUnitChip(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials to buy', n: '02',
    columns: [
      col.seq(), col.item(), col.desc(),
      // Worked out on save from the item, its category and the settings — the
      // buyer reads them, and does not type them.
      { key: 'abcClass', label: 'Class', type: 'text', width: 76 },
      { key: 'sourceQty', label: 'Requested', type: 'qty', width: 110, align: 'right' },
      { key: 'stockOnHand', label: 'In stock', type: 'qty', width: 104, align: 'right' },
      col.qty('To buy'),
      col.unit(),
      { key: 'requiredBy', label: 'Needed by', type: 'date', width: 136, editable: true,
        hint: 'Left empty, the date on the header.' },
      { key: 'leadTimeDays', label: 'Lead time', type: 'text', width: 100, align: 'right' },
      { key: 'orderBy', label: 'Order by', type: 'date', width: 128 },
      { key: 'route', label: 'Buy through', type: 'text', width: 170, editable: true,
        options: PROCUREMENT_ROUTES.map(([value, label]) => ({ value, label })) },
      col.rate('Estimated rate'),
      col.amount('Estimated value'),
      { key: 'forProjectId', label: 'For', type: 'text', width: 180, editable: true, lookup: { entity: 'project' },
        hint: 'Which job this is bought for, on a requisition that covers several.' },
      col.boqRef(),
    ],
    sources: [
      { key: 'request', label: 'Pick from material requests', source: 'request' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  columns: [
    ...baseColumns(null, 'Estimated value'),
    { key: 'fulfilmentStatus', label: 'Progress', type: 'text', width: 210 },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'purchaseExecutiveId', label: 'Purchase executive', control: 'lookup', lookup: { entity: 'employee' } },
  ],
  actions: [...BASIC_ACTIONS, 'clone', 'preClose', 'undoPreClose', 'changeTaxUnit'],
  // The purchase plan gates buying from a request, whether the order is raised
  // from the request or through a requisition.
  rules: ['excess_purchase_quantity', 'purchase_plan_required'],
  allocatesFrom: ['material_request'],
  createsNext: [
    { docType: 'request_for_quote', label: 'Request for quote', whenStatus: 'APPROVED' },
    { docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' },
  ],
} as const)

/**
 * docs/24 §3.6 — how a supplier has done, weighed criterion by criterion.
 *
 * Each row is one criterion from the master with its weight. The system
 * fills in what it can measure — on-time deliveries, what passed inspection,
 * quotations won, paperwork in order, the credit given, orders completed —
 * and suggests a score out of five from it; the evaluator scores the rest and
 * may overrule a suggestion. The weighted total out of 100 grades the
 * supplier, and the latest approved evaluation is the one the comparative
 * statement weighs against price.
 */
export const vendorEvaluation = defineDocument({
  key: 'vendor_evaluation',
  group: 'Purchase and Expense',
  blurb: 'Score a supplier or subcontractor on quality, price, delivery, compliance and the rest.',
  route: 'vendor_evals',
  label: 'Vendor evaluation',
  labelPlural: 'Vendor evaluations',
  numbering: 'VENDOR_EVALUATION',
  numberPrefix: 'VE/',
  workflow: 'VENDOR_EVALUATION',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Evaluate a vendor',
    subtitle: 'Choose who, and the period their orders and deliveries are measured over.',
    fields: [
      f.radio('vendorKind', 'Who is being evaluated?', [['SUPPLIER', 'A supplier'], ['SUBCONTRACTOR', 'A subcontractor']]),
      { ...f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
        visibleWhen: { field: 'vendorKind', equals: ['SUPPLIER'] } },
      { ...f.look('supplierId', 'Subcontractor', 'subcontractor', true, 'full'),
        visibleWhen: { field: 'vendorKind', equals: ['SUBCONTRACTOR'] } },
      f.date('periodFrom', 'Measured from', true),
      f.date('periodTo', 'Measured to', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        f.choice('vendorKind', 'Vendor', [['SUPPLIER', 'Supplier'], ['SUBCONTRACTOR', 'Subcontractor']], false),
        f.look('supplierId', 'Vendor', 'party', true),
        f.date('documentDate', 'Evaluated on', true),
        f.date('periodFrom', 'Measured from', true),
        f.date('periodTo', 'Measured to', true),
        f.look('evaluatedById', 'Evaluated by', 'employee'),
        { key: 'weightedScore', label: 'Score (out of 100)', control: 'text', width: 'half',
          hint: 'Worked out from the criteria below when the evaluation is saved.' },
        { key: 'grade', label: 'Grade', control: 'text', width: 'half',
          hint: 'A 80 and over — preferred; B 60 — approved; C 40 — conditional; D — not recommended.' },
        f.notes('strengths', 'Strengths'),
        f.notes('improvements', 'What must improve'),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Criteria', n: '02',
    addLabel: 'Add a criterion',
    totalsAs: 'NONE',
    columns: [
      col.seq(),
      { key: 'criterionId', label: 'Criterion', type: 'text', width: 260, editable: true,
        lookup: { entity: 'vendor_criterion', fills: { weight: 'weight', measure: 'measure' } } },
      { key: 'weight', label: 'Weight %', type: 'rate', width: 110, align: 'right', editable: true },
      { key: 'measured', label: 'What the records show', type: 'text', width: 300 },
      { key: 'suggestedScore', label: 'Suggested', type: 'text', width: 100, align: 'right' },
      { key: 'score', label: 'Score', type: 'text', width: 150, editable: true,
        options: [
          { value: '5', label: '5 — Excellent' },
          { value: '4', label: '4 — Very good' },
          { value: '3', label: '3 — Good' },
          { value: '2', label: '2 — Fair' },
          { value: '1', label: '1 — Poor' },
        ],
        hint: 'Left empty, the suggestion is used where there is one.' },
      { key: 'points', label: 'Points', type: 'rate', width: 100, align: 'right' },
      { key: 'remarks', label: 'Remarks', type: 'text', width: 220, editable: true },
    ],
    sources: [{ key: 'criteria', label: 'Load the criteria', source: 'criteria' }],
  },
  // A score, not a value, and no project: an evaluation is of the vendor.
  columns: [
    ...baseColumns('supplier', 'Vendor').filter((c) => c.key !== 'total' && c.key !== 'projectName'),
    { key: 'weightedScore', label: 'Score', type: 'text' as const, width: 110, align: 'right' as const },
    { key: 'grade', label: 'Grade', type: 'text' as const, width: 100 },
  ],
  filters: baseFilters('supplier', 'Vendor').filter((f) => f.key !== 'projectId'),
  actions: BASIC_ACTIONS,
  rules: ['evaluation_weights'],
} as const)

/**
 * docs/24 §3.7 — the supplier telling us what is on its way.
 *
 * "Supplier processing and dispatch": between the order and the receipt the
 * buyer's only question is where the goods are, and the answer was in a phone
 * call. A dispatch advice records it against the order — what was sent, when,
 * with which transporter under which LR number and e-way bill, and the
 * invoice that travels with it — so expediting is a list rather than a round
 * of calls, and the receipt note at the gate starts from the paperwork the
 * supplier already sent.
 *
 * It spends nothing on the order: the receipt does that. A dispatch that never
 * arrives leaves the order exactly as open as it was.
 */
export const supplierDispatch = defineDocument({
  key: 'supplier_dispatch',
  group: 'Purchase and Expense',
  blurb: 'Record what a supplier has made ready or sent against an order: transporter, LR no, e-way bill, invoice.',
  route: 'sup_dispatches',
  label: 'Supplier dispatch',
  labelPlural: 'Supplier dispatches',
  numbering: 'SUPPLIER_DISPATCH',
  numberPrefix: 'DSP/',
  workflow: 'SUPPLIER_DISPATCH',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record a supplier dispatch',
    subtitle: 'Choose the order the goods are being sent against. Its open lines can be pulled in.',
    fields: [
      f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
      f.look('purchaseOrderId', 'Purchase order', 'purchase_order', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'Dispatch', fields: [
        { ...f.look('supplierId', 'Supplier', 'supplier', true), lockAfterDraft: true },
        { ...f.look('purchaseOrderId', 'Against order', 'purchase_order', true), lockAfterDraft: true },
        f.date('documentDate', 'Advised on', true),
        f.choice('dispatchStatus', 'Where it is', [
          ['IN_PRODUCTION', 'In production'],
          ['READY', 'Ready to dispatch'],
          ['DISPATCHED', 'Dispatched — in transit'],
          ['DELIVERED', 'Delivered'],
        ]),
        { ...f.date('expectedDispatchDate', 'Expected to dispatch on'),
          visibleWhen: { field: 'dispatchStatus', equals: ['IN_PRODUCTION', 'READY'] } },
        { ...f.date('dispatchedOn', 'Dispatched on'),
          visibleWhen: { field: 'dispatchStatus', equals: ['DISPATCHED', 'DELIVERED'] } },
        f.date('expectedArrival', 'Expected on site'),
        f.text('transporter', 'Transporter'),
        f.text('lrNo', 'LR / AWB no'),
        f.text('vehicleNo', 'Vehicle no'),
        f.text('driverContact', 'Driver contact'),
        f.text('ewayBillNo', 'e-Way bill no'),
        f.text('supplierInvoiceNo', "Supplier's invoice no"),
        f.date('supplierInvoiceDate', "Supplier's invoice date"),
        f.yesNo('testCertificateSent', 'Test certificate / TDS sent with the goods'),
        f.yesNo('packingListSent', 'Packing list sent with the goods'),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials dispatched', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      col.ordered(), col.open(),
      col.qty('Dispatched'), col.unit(false), col.rate('Rate', false), col.amount(),
    ],
    sources: [{ key: 'order', label: 'Pull the open order lines', source: 'order' }],
  },
  columns: [
    ...baseColumns('supplier', 'Supplier', 'Value'),
    { key: 'dispatchStatus', label: 'Where it is', type: 'text', width: 170 },
    { key: 'expectedArrival', label: 'Expected', type: 'date', width: 128 },
  ],
  filters: baseFilters('supplier', 'Supplier'),
  actions: BASIC_ACTIONS,
  rules: [],
  // A link to the order line, for "pull the open lines" and for expediting;
  // `v_line_link.consumes` is false for it, so the order stays open.
  allocatesFrom: ['purchase_order'],
  createsNext: [{ docType: 'goods_receipt', label: 'Receipt note (GRN)', whenStatus: 'APPROVED' }],
} as const)

/** docs/06 §1 — ask several suppliers to price the same list. */
export const requestForQuote = defineDocument({
  key: 'request_for_quote',
  group: 'Purchase and Expense',
  blurb: 'Create an RFQ for materials. You can add estimated or requested materials from the project.',
  route: 'rfq',
  label: 'Request for quote',
  labelPlural: 'Requests for quote',
  numbering: 'RFQ',
  numberPrefix: 'RFQ-{CO} ',
  workflow: 'RFQ',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Request for quote',
    subtitle: 'An RFQ can be for a confirmed project, for an opportunity still under quotation, or a periodic price check.',
    fields: [
      f.radio('rfqFor', 'RFQ for?', [['PROJECT', 'Project'], ['GENERAL', 'General']]),
      { ...f.look('projectId', 'Project', 'project', true, 'full'),
        visibleWhen: { field: 'rfqFor', equals: ['PROJECT'] } },
    ],
  },
  form: {
    sections: [
      // The reference's cards, in its order (PUR-001): General Info, Address
      // and Correspondence Info, then the materials, Additional Info and the
      // supplier list under them.
      { key: 'general', n: '01', title: 'General info', fields: [
        f.date('documentDate', 'Document date', true),
        f.date('responseDueDate', 'Response due date', true),
        // Printed on the RFQ as "Delivery Period" (PRN-024).
        f.text('deliveryTimeline', 'Delivery timeline', 'half', true),
        // One enquiry for several jobs (PUR-015). Starts on the project the
        // RFQ was raised for; the first is the RFQ's project for scoping.
        { key: 'projectIds', label: 'Projects', control: 'lookup', width: 'half', required: true,
          lookup: { entity: 'project', multiple: true },
          visibleWhen: { field: 'rfqFor', equals: ['PROJECT'] } },
        f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true),
        // Ours, after the reference's own (PUR-002).
        { ...f.choice('rfqFor', 'Type', [['PROJECT', 'Project'], ['GENERAL', 'General']], false), lockAfterDraft: true },
        // "Close the RFQ after the process is over" — after which the supplier
        // portal stops accepting, whatever the invitation link says
        // (docs/video-notes/06 §5).
        { ...f.choice('rfqStatus', 'Is this still open?', [['OPEN', 'Open'], ['CLOSED', 'Closed']], false),
          hint: 'Closing it stops suppliers responding, without cancelling the request.' },
        jobType(),
        taxUnitChip(),
        f.notes(),
      ] },
      { key: 'address', n: '02', title: 'Address and correspondence info', fields: [
        scoped(f.look('billingAddressId', 'Billing address', 'address', true), OURS),
        scoped(f.look('deliveryAddressId', 'Delivery address', 'address', true), OURS),
        scoped(f.look('contactId', 'Company contact', 'contact', true), OURS),
      ] },
      // PUR-019 — what the supplier is told, and which charges it must quote.
      { key: 'additional', n: '04', title: 'Additional info', placement: 'afterLines', fields: [
        f.notes('notesToSupplier', 'Notes to supplier'),
        { key: 'expectedChargeIds', label: 'Additional charges - expected', control: 'lookup', width: 'full',
          lookup: { entity: 'charge', multiple: true },
          hint: 'The charges each supplier is asked to quote. Every quotation against this RFQ starts with them.' },
      ] },
    ],
  },
  lines: {
    // "Rate in <billing unit>": which unit the supplier quotes in (PUR-048).
    ...withBillingUnit(pricedLines('Material info', '03'), true),
    // The vendor's three sources: what was estimated, what the site actually
    // asked for, and anything at all (docs/video-notes/06 §5).
    sources: [
      { key: 'estimation', label: 'Pick from the estimate', source: 'estimation' },
      { key: 'request', label: 'Pick from material requests', source: 'request' },
      { key: 'requisition', label: 'Pick from purchase requisitions', source: 'requisition' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  // The charges a supplier is asked to quote are named above, not priced
  // here: the RFQ asks, the quotation answers (PUR-019).
  grids: [participantGrid],
  // The reference's list: due date, Project or General, and whether it is
  // still taking responses (PUR-012).
  columns: [
    { key: 'documentNo', label: 'Document number', type: 'docno', width: 152 },
    { key: 'documentDate', label: 'Document date', type: 'date', width: 128 },
    { key: 'responseDueDate', label: 'Response due', type: 'date', width: 136 },
    { key: 'rfqFor', label: 'Type', type: 'text', width: 110 },
    { key: 'projectNames', label: 'Projects', type: 'text' },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'rfqOngoing', label: 'Ongoing?', type: 'text', width: 100 },
    { key: 'total', label: 'Estimated value', type: 'money', width: 150, align: 'right' },
    { key: 'fulfilmentStatus', label: 'Progress', type: 'text', width: 210, optional: true },
    sentColumn(true),
  ],
  filters: [
    ...baseFilters(null),
    { key: 'responseDueDate', label: 'Response due', control: 'dateRange' },
    { key: 'rfqFor', label: 'Type', control: 'select', options: [{ value: 'PROJECT', label: 'Project' }, { value: 'GENERAL', label: 'General' }] },
    { key: 'rfqOngoing', label: 'Ongoing?', control: 'select', options: [...YES_NO] },
    { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
  ],
  actions: [...SENDABLE_ACTIONS, 'changeTaxUnit'],
  rules: [],
  requiresSetting: 'rfq_enabled',
  createsNext: [{ docType: 'supplier_quotation', label: 'Supplier quotation', whenStatus: 'APPROVED' }],
} as const)

/** docs/06 §2 — one supplier's answer to an RFQ. */
export const supplierQuotation = defineDocument({
  key: 'supplier_quotation',
  group: 'Purchase and Expense',
  blurb: 'Create supplier RFQ responses as quotations. RFQ online response form will automatically create quotations.',
  route: 'sup_quotes',
  label: 'Supplier quotation',
  labelPlural: 'Supplier quotations',
  numbering: 'SUPPLIER_QUOTATION',
  numberPrefix: 'Quote-',
  workflow: 'SUPPLIER_QUOTATION',
  tabs: REVISABLE_TABS,
  createWizard: {
    title: 'Supplier quotation',
    subtitle: 'Choose the RFQ and the supplier. Only one quotation is allowed per participant.',
    fields: [
      // Only an RFQ still taking quotations: not one Done and Closed.
      narrowed(f.look('requestForQuoteId', 'Request for quote', 'request_for_quote', true, 'full'), stillOpen()),
      // Only the RFQ's participants that have not quoted yet (PUR-026).
      scoped(f.look('supplierId', 'Supplier', 'supplier', true, 'full'), [{ owner: 'RFQ', field: 'requestForQuoteId' }], 'none'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        // Fixed by the RFQ it answers (PUR-027, PUR-016): the lines, type,
        // projects and purchase executive are copied from it when the
        // quotation is created (quotation-from-rfq.ts), and read as copied.
        { ...f.look('requestForQuoteId', 'RFQ', 'request_for_quote', true), lockAfterDraft: true },
        { ...f.look('supplierId', 'Supplier', 'supplier', true), lockAfterDraft: true },
        { ...f.choice('rfqFor', 'Type', [['PROJECT', 'Project'], ['GENERAL', 'General']]), lockAfterDraft: true },
        { key: 'projectIds', label: 'Projects', control: 'lookup', width: 'half', readOnly: true,
          lookup: { entity: 'project', multiple: true } },
        { ...f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true), prefill: 'none' as const, readOnly: true,
          hint: 'The RFQ’s purchase executive.' },
        f.date('documentDate', 'Document date', true),
        f.text('supplierQuoteNo', 'Supplier quote no'),
        f.date('validUpto', 'Valid upto'),
        f.notes('supplierNotes', 'Notes from supplier'),
        f.notes(),
        // Ours, after the reference's own.
        f.choice('decision', 'Decision', [['', 'Not decided'], ['AWARDED', 'Awarded'], ['REJECTED', 'Rejected']], false),
        taxUnitChip(),
      ] },
      { key: 'billing', n: '02', title: 'Billing and delivery info', fields: [
        f.text('deliveryLeadTime', 'Delivery lead time'),
        f.text('paymentTerms', 'Payment terms'),
        // docs/24 §3.5 — what this supplier offered, which the comparison weighs;
        // left empty the supplier record's usual credit is assumed.
        { key: 'creditPeriodDays', label: 'Credit offered (days)', control: 'number', width: 'half',
          hint: 'Left empty, the credit period on the supplier record.' },
      ] },
      // An import supplier quotes in its own currency (PUR-028).
      currencySection(),
    ],
  },
  lines: {
    ...pricedLines('Material info', '03'),
    // Every RFQ line is copied in; whether the supplier priced it is said
    // here. Left empty, a line with a rate is Yes and one without is No.
    // A rate quoted in another unit is said in the billing columns (PUR-048).
    columns: [
      ...withBillingUnit(pricedLines('Material info', '03')).columns,
      { key: 'responded', label: 'Responded', type: 'text', width: 120, editable: true,
        options: [{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }] },
    ],
  },
  grids: [chargesGrid('04')],
  // The reference's list, and its "Create View" catalogue (PUR-024, PUR-018).
  columns: [
    { key: 'supplierName', label: 'Supplier', type: 'text' },
    { key: 'documentNo', label: 'Document number', type: 'docno', width: 152 },
    { key: 'documentDate', label: 'Document date', type: 'date', width: 128 },
    { key: 'validUpto', label: 'Valid upto', type: 'date', width: 128 },
    { key: 'supplierQuoteNo', label: 'Supplier quote no', type: 'text', width: 160 },
    { key: 'projectNames', label: 'Projects', type: 'text' },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'total', label: 'Quoted value', type: 'money', width: 150, align: 'right' },
    { key: 'createdFromPortal', label: 'From the portal', type: 'text', width: 150 },
    { ...opt('decision', 'Decision', 'text', 130) },
    { ...opt('requestForQuoteId', 'RFQ', 'text', 170) },
    { ...opt('rfqFor', 'Type', 'text', 110) },
    { ...opt('purchaseExecutiveId', 'Purchase executive', 'text', 180) },
    { ...opt('paymentTerms', 'Payment terms', 'text', 180) },
    { ...opt('deliveryLeadTime', 'Delivery lead time', 'text', 150) },
    { ...opt('creditPeriodDays', 'Credit period', 'text', 120) },
    { ...opt('interstate', 'Interstate (IGST)?', 'text', 140) },
    { ...opt('supplierNotes', 'Notes from supplier', 'text', 240) },
    { ...opt('taxUnitId', 'Tax unit', 'text', 200) },
    { ...opt('jobTypeName', 'Job types', 'text', 180) },
    { ...opt('remarks', 'Remarks', 'text', 240) },
    ...fcyColumns('Quoted value'),
  ],
  filters: [
    ...baseFilters('supplier', 'Supplier'),
    { key: 'validUpto', label: 'Valid upto', control: 'dateRange' },
    { key: 'supplierQuoteNo', label: 'Supplier quote no', control: 'text' },
    { key: 'rfqFor', label: 'Type', control: 'select', options: [{ value: 'PROJECT', label: 'Project' }, { value: 'GENERAL', label: 'General' }] },
    { key: 'requestForQuoteId', label: 'RFQ', control: 'lookup', lookup: { entity: 'request_for_quote' } },
  ],
  actions: [...FULL_ACTIONS, 'changeCurrency', 'changeTaxUnit'],
  rules: ['one_quotation_per_participant', 'quotation_from_participant'],
  requiresSetting: 'rfq_enabled',
  // Each quoted line points at the RFQ line it prices — "Pull the open lines"
  // reads the named RFQ — which is what the comparison compares (docs/24 §3.5).
  // The link spends nothing: every supplier prices the same line.
  allocatesFrom: ['request_for_quote'],
  createsNext: [{ docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' }],
} as const)

/** docs/06 §3 — agreed prices, valid for a period. */
export const supplierRateContract = defineDocument({
  key: 'supplier_rate_contract',
  group: 'Purchase and Expense',
  blurb: 'Create rate contracts for materials. Each contract includes the supplier, material cost, and validity period details.',
  route: 'sup_rate_contracts',
  label: 'Supplier rate contract',
  labelPlural: 'Supplier rate contracts',
  numbering: 'SUPPLIER_RATE_CONTRACT',
  numberPrefix: 'SRC-',
  workflow: 'SUPPLIER_RATE_CONTRACT',
  tabs: REVISABLE_TABS,
  createWizard: {
    title: 'Supplier rate contract',
    fields: [
      f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
      f.date('validTill', 'Valid till', true, 'full'),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        f.look('supplierId', 'Supplier', 'supplier', true),
        f.date('documentDate', 'Document date', true),
        f.date('validTill', 'Valid till', true),
        f.text('supplierDocNo', "Supplier's document no"),
        scoped(f.look('contactId', 'Supplier contact', 'contact'), ofParty('supplierId')),
        scoped(f.look('supplierAddressId', 'Supplier address', 'address'), ofParty('supplierId')),
        taxUnitChip(),
        f.text('paymentTerms', 'Delivery and payment terms', 'full'),
        f.notes(),
      ] },
      // Agreed in the supplier's currency where it is not ours (PUR-030).
      currencySection(),
    ],
  },
  lines: {
    // "Material Rate Info", with "Import data" and "Export data" (PUR-023).
    title: 'Material rate info', n: '02',
    exportable: true, exportLabel: 'Export data',
    columns: [col.seq(), col.item(), col.desc(), col.brand(), col.unit(), col.rate('Agreed rate')],
    sources: [{ key: 'excel', label: 'Import data', source: 'excel' }],
  },
  // A rate contract is with a supplier, not for a job: no Project column, and
  // its validity and the supplier's own number instead (PUR-031).
  columns: [
    { key: 'documentNo', label: 'Document number', type: 'docno', width: 152 },
    { key: 'documentDate', label: 'Document date', type: 'date', width: 128 },
    { key: 'supplierName', label: 'Supplier', type: 'text' },
    { key: 'validTill', label: 'Valid till', type: 'date', width: 128 },
    { key: 'supplierDocNo', label: "Supplier's document no", type: 'text', width: 180 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'total', label: 'Contract value', type: 'money', width: 150, align: 'right' },
    { ...opt('paymentTerms', 'Delivery and payment terms', 'text', 220) },
    ...fcyColumns('Contract value'),
  ],
  filters: [
    ...baseFilters('supplier', 'Supplier').filter((x) => x.key !== 'projectId'),
    { key: 'validTill', label: 'Valid till', control: 'dateRange' },
    { key: 'supplierDocNo', label: "Supplier's document no", control: 'text' },
    { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
  ],
  actions: [...FULL_ACTIONS, 'changeCurrency', 'changeTaxUnit'],
  rules: [],
} as const)

/** docs/06 §5 — labour and installation, priced against billing terms. */
export const subcontractOrder = defineDocument({
  key: 'subcontract_order',
  group: 'Purchase and Expense',
  blurb: 'Create new orders for estimated subcontract items. You can also view existing orders.',
  route: 'sub_ords',
  label: 'Subcontract order',
  labelPlural: 'Subcontract orders',
  numbering: 'SUBCONTRACT_ORDER',
  numberPrefix: 'WO/{CO}',
  workflow: 'SUBCONTRACT_ORDER',
  tabs: REVISABLE_TABS,
  createWizard: {
    title: 'Subcontract order',
    subtitle: 'Select the project and the supplier.',
    fields: [
      f.look('projectId', 'Project', 'project', true),
      // "Supplier", as the reference asks it; the picker still offers
      // subcontractors (PUR-041).
      f.look('supplierId', 'Supplier', 'subcontractor', true),
      { ...f.radio('billingTermsApplicable', 'Billing terms applicable?', [['YES', 'Yes'], ['NO', 'No']]), defaultValue: 'NO' },
      f.radio('scope', 'Scope', [['INSTALLATION', 'Installation'], ['SUPPLY_AND_INSTALLATION', 'Supply and installation']]),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.look('supplierId', 'Supplier', 'subcontractor', true),
        f.date('documentDate', 'Document date', true),
        f.choice('scope', 'Scope', [['INSTALLATION', 'Installation'], ['SUPPLY_AND_INSTALLATION', 'Supply and installation']]),
        f.choice('billingTermsApplicable', 'Billing terms applicable?', [['YES', 'Yes'], ['NO', 'No']], false),
        f.look('supplierBillingTermId', 'Supplier billing term', 'supplier_billing_term'),
        jobType(),
        taxUnitChip(),
        f.look('purchaseExecutiveId', 'Purchase executive', 'employee'),
        f.date('dueDate', 'Due by'),
        // What the printed order states (PRN-025): the subcontractor's own
        // quote it answers, how they will be paid, and — for a rate contract —
        // that the quantities are open and only the rates are agreed.
        f.text('supplierQuoteNo', 'Supplier quote no'),
        f.text('paymentTerms', 'Payment terms'),
        f.yesNo('isRateContract', 'Rate contract?', 'Rates agreed with open quantities. The printed order shows RC where a quantity would be.'),
        interstate(),
        f.notes('notesToSupplier', 'Notes to supplier'),
        f.notes(),
      ] },
      { key: 'delivery', n: '02', title: 'Billing and delivery info', fields: [
        scoped(f.look('deliveryAddressId', 'Work site', 'address', true), OURS, 'none'),
        scoped(f.look('contactId', 'Site contact', 'contact'), OURS, 'none'),
        scoped(f.look('billingAddressId', 'Billing address', 'address'), OURS),
        scoped(f.look('supplierContactId', 'Supplier contact', 'contact'), ofParty('supplierId')),
      ] },
      currencySection(),
      // Terms and Conditions close the form, under the work (PUR-026).
      { ...termsSection(), placement: 'afterLines' },
    ],
  },
  // "Items : 1 Subcontract(s) added" on the Summary (PUR-035).
  lines: { ...withBillingUnit(pricedLines('Scope of work', '03', 'Work item')), countAs: { label: 'Items', noun: 'Subcontract(s)' } },
  grids: [chargesGrid(), adjustmentsGrid()],
  // Supplier first, then Job type and Sent to supplier, as the reference
  // lists them (SUB-001, PUR-042).
  columns: [
    { key: 'supplierName', label: 'Supplier', type: 'text' },
    ...baseColumns(null).flatMap((c) => (c.key === 'projectName'
      ? [c, { key: 'jobTypeName', label: 'Job type', type: 'text' as const, width: 180 }]
      : [c])),
    sentColumn(),
    { key: 'fulfilmentStatus', label: 'Progress', type: 'text', width: 210 },
    invoiceStatusColumn(),
  ],
  filters: [
    ...baseFilters('subcontractor', 'Supplier'),
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
    sentFilter(),
    invoiceStatusFilter(),
  ],
  actions: [...SYNCED_ACTIONS, 'preClose', 'undoPreClose'],
  rules: ['rate_exceeds_estimation', 'excess_purchase_quantity', 'supplier_not_approved'],
  createsNext: [
    { docType: 'work_progress', label: 'Work progress', whenStatus: 'APPROVED' },
    { docType: 'purchase_invoice', label: 'Purchase invoice', whenStatus: 'APPROVED' },
  ],
} as const)

/** docs/17 §2 — measured progress against a subcontract, fully priced and taxed. */
/**
 * The JMR — the joint measurement record a claimed quantity comes from.
 *
 * "Create the work progress of the installation based on JMR (Measurement
 * Reading)" is the vendor's own wording, and it is the step that makes a
 * subcontractor's claim checkable. Without it the quantity on the progress
 * line is an assertion, and the engineer who thinks it is wrong has nothing to
 * argue with (docs/video-notes/06 §3).
 *
 * Each row measures one location: how many of a thing, and its dimensions. The
 * total is checked against the quantity claimed on the work item it hangs off.
 */
const measurementGrid: LineGridDef = {
  section: 'MEASUREMENT',
  parentSection: 'ITEM',
  title: 'Measurement sheet (JMR)', n: '03',
  addLabel: 'Add a measurement',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'parentLineId', label: 'For which work', type: 'text', width: 240, editable: true,
      parentPicker: true,
      hint: 'The line above that this measurement counts towards.' },
    { key: 'description', label: 'Location', type: 'text', editable: true,
      hint: 'Where on site this was measured — grid line, floor, room.' },
    { key: 'nos', label: 'Nos', type: 'qty', width: 88, align: 'right', editable: true },
    { key: 'lengthM', label: 'Length', type: 'qty', width: 96, align: 'right', editable: true },
    { key: 'breadthM', label: 'Breadth', type: 'qty', width: 100, align: 'right', editable: true },
    { key: 'depthM', label: 'Depth', type: 'qty', width: 92, align: 'right', editable: true },
    // Nos x Length x Breadth x Depth, worked out by the server. Typed by hand
    // it was a number beside four others that did not have to agree with it,
    // and the mismatch rule was checking one assertion against another.
    col.qty('Measured', false),
    col.unit(),
  ],
  // A JMR is a sheet the engineer and the contractor walk the site with.
  sources: [{ key: 'excel', label: 'Import measurements from Excel', source: 'excel' }],
}

export const workProgress = defineDocument({
  key: 'work_progress',
  group: 'Purchase and Expense',
  blurb: "Record progress of subcontracts. Review the supplier's invoice against the recorded progress.",
  route: 'work_progress',
  label: 'Work progress',
  labelPlural: 'Work progress',
  numbering: 'WORK_PROGRESS',
  numberPrefix: 'WP/',
  workflow: 'WORK_PROGRESS',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record work progress',
    subtitle: 'Progress against a subcontractor’s order, or against work your own people did.',
    fields: [
      /*
        Three cases, not two. The third is the common one on a live site and
        used to have nowhere to go: several contractors working the same job
        with no order issued to any of them, their quantities recorded daily by
        our own engineer against the BOQ, and none of it ever becoming a
        subcontractor bill (docs/video-notes/06 §3).
      */
      f.radio('progressFor', 'Whose work is this?', [
        ['SUBCONTRACTOR', "A subcontractor's, against their order"],
        ['CONTRACTOR_NO_ORDER', 'A contractor with no order — measured by us'],
        ['OWN_LABOUR', 'Our own labour'],
      ]),
      { ...f.look('supplierId', 'Subcontractor', 'subcontractor', true),
        visibleWhen: { field: 'progressFor', equals: ['SUBCONTRACTOR'] } },
      { ...f.look('subcontractOrderId', 'Subcontract order', 'subcontract_order', true),
        visibleWhen: { field: 'progressFor', equals: ['SUBCONTRACTOR'] } },
      { ...f.look('supplierId', 'Contractor', 'subcontractor', true),
        hint: 'Who did the work. No order is raised and no invoice follows from this.',
        visibleWhen: { field: 'progressFor', equals: ['CONTRACTOR_NO_ORDER'] } },
      { ...f.look('projectId', 'Project', 'project', true),
        visibleWhen: { field: 'progressFor', equals: ['CONTRACTOR_NO_ORDER', 'OWN_LABOUR'] } },
      f.date('documentDate', 'As on date', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        f.choice('progressFor', 'Whose work is this?', [
          ['SUBCONTRACTOR', 'A subcontractor'],
          ['CONTRACTOR_NO_ORDER', 'A contractor with no order'],
          ['OWN_LABOUR', 'Our own labour'],
        ], false),
        f.look('subcontractOrderId', 'Subcontract order', 'subcontract_order'),
        f.look('supplierId', 'Contractor', 'subcontractor'),
        f.date('documentDate', 'As on date', true),
        f.date('periodFrom', 'Period from'),
        f.choice('progressMethod', 'Progress measured as', [['CUMULATIVE', 'Cumulative — total done to date'], ['MINIMUM', 'This period only']]),
        f.look('projectId', 'Project', 'project', true),
        /*
          How many people were on site for this.

          "We are not giving it like a lump sum. We have to calculate how many
          people are coming in daily — we need those reports." Recorded here
          rather than as its own document because it is the same entry, made by
          the same person, about the same day's work
          (docs/video-notes/06 §5).
        */
        { key: 'labourCount', label: 'People on site', control: 'number', width: 'half',
          hint: 'How many worked on this today. Left empty, nothing is counted for the day.' },
        { key: 'labourHours', label: 'Hours worked', control: 'number', width: 'half',
          hint: 'Total man-hours, where the contract is measured in them.' },
        jobType(),
        taxUnitChip(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Work completed', n: '02',
    columns: [
      col.seq(), col.item('Work item'), col.desc(), col.boqRef(),
      { key: 'supplierBillingTermId', label: 'Billing term', type: 'text', width: 170, editable: true,
        lookup: { entity: 'supplier_billing_term' } },
      col.qty('Completed'), col.unit(), col.rate(), col.hsn(), col.tax(), col.amount(),
    ],
    /*
      Two ways in, because there are two ways work happens.

      Against an order, the order's own lines are pulled. Without one — our own
      labour, or a contractor nobody issued an order to — the estimate is the
      only thing that knows what the BOQ promised, and picking from it is what
      links the progress back to the BOQ line. Typed by hand the entry has no
      link at all: it would show no BOQ reference in the contractor report, and
      it would never move the Calculated quantity on a billing request, which
      is measured from exactly these links (docs/video-notes/06 §3, §7).
    */
    sources: [
      { key: 'order', label: 'Pull the order lines', source: 'order' },
      { key: 'estimation', label: 'Pick from the estimated work', source: 'estimation' },
    ],
  },
  grids: [measurementGrid, chargesGrid()],
  columns: baseColumns('subcontractor', 'Subcontractor', 'Progress value'),
  filters: baseFilters('subcontractor', 'Subcontractor'),
  actions: [...BASIC_ACTIONS, 'changeTaxUnit'],
  rules: ['excess_work_progress_quantity', 'progress_measurement_mismatch'],
  allocatesFrom: ['subcontract_order'],
  createsNext: [{ docType: 'purchase_invoice', label: 'Purchase invoice', whenStatus: 'APPROVED' }],
} as const)

/** docs/17 §3 — the actual against an estimated overhead line. */
export const otherCost = defineDocument({
  key: 'other_cost',
  group: 'Purchase and Expense',
  blurb: 'Record project costs other than purchases and subcontracts.',
  route: 'other_costs',
  label: 'Other cost',
  labelPlural: 'Other costs',
  numbering: 'OTHER_COST',
  numberPrefix: 'OC/',
  workflow: 'OTHER_COST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record another cost',
    subtitle: 'For cost that is neither a purchase nor a subcontract — consumables, fittings, hire.',
    fields: [
      f.look('projectId', 'Project', 'project', true, 'full'),
      f.date('documentDate', 'Date', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.date('documentDate', 'Date', true),
        f.look('supplierId', 'Paid to', 'supplier'),
        jobType(),
        taxUnitChip(),
        interstate(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Costs', n: '02',
    columns: [
      col.seq(),
      { key: 'itemId', label: 'Cost head', type: 'text', editable: true, lookup: { entity: 'item' } },
      col.desc(), col.boqRef(), col.qty(), col.unit(), col.rate(), col.hsn(), col.tax(), col.amount('Amount'),
    ],
    sources: [
      { key: 'estimation', label: 'Pick from the estimated overheads', source: 'estimation' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  columns: baseColumns('supplier', 'Paid to', 'Cost'),
  filters: baseFilters('supplier', 'Paid to'),
  actions: [...SYNCED_BASIC_ACTIONS, 'changeTaxUnit'],
  rules: [],
} as const)

/** docs/17 §4 — staff expenses and advances: one document, two modes. */
export const expenseReport = defineDocument({
  key: 'expense_report',
  group: 'Purchase and Expense',
  blurb: 'Report your project and service expenses here, and request any advances you need.',
  route: 'expense_reports',
  label: 'Expense report',
  labelPlural: 'Expense reports',
  numbering: 'EXPENSE_REPORT',
  numberPrefix: 'EXP/',
  workflow: 'EXPENSE_REPORT',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create an expense report',
    subtitle: 'Claiming what you spent, or asking for money up front.',
    fields: [
      f.radio('expenseType', 'Which is this?', [
        ['EXPENSE_REPORT', 'An expense report — money already spent'],
        ['ADVANCE_PAYMENT', 'An advance — money needed up front'],
      ]),
      f.look('employeeId', 'Whose is it?', 'employee', true, 'full'),
      f.look('projectId', 'Project', 'project', false, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        f.choice('expenseType', 'Which is this?', [['EXPENSE_REPORT', 'Expense report'], ['ADVANCE_PAYMENT', 'Advance payment']], false),
        f.look('employeeId', 'Employee', 'employee', true),
        f.look('projectId', 'Project', 'project'),
        // What the money was spent on — a project, or service work — which
        // the Employee advances and expenses report filters by (RPT-127).
        // Left empty, a claim naming a project is a project one.
        f.choice('relatedTo', 'Related to', [['PROJECT', 'Project'], ['SERVICE', 'Service']], false),
        f.date('documentDate', 'Claim date', true),
        f.date('periodFrom', 'Period from'),
        f.date('periodTo', 'Period to'),
        { ...f.money('advanceRequested', 'Advance requested'),
          visibleWhen: { field: 'expenseType', equals: ['ADVANCE_PAYMENT'] } },
        taxUnitChip(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Expenses', n: '02',
    columns: [
      col.seq(),
      { key: 'itemId', label: 'Expense head', type: 'text', editable: true, lookup: { entity: 'item' } },
      col.desc(),
      { key: 'incurredOn', label: 'Incurred on', type: 'date', width: 130, editable: true },
      col.amount(),
    ],
    sources: [{ key: 'excel', label: 'Import from Excel', source: 'excel' }],
  },
  grids: [{
    section: 'ADJUSTMENT',
    title: 'Advances settled against this claim', n: '03',
    addLabel: 'Settle an advance',
    totalsAs: 'SUBTRACT',
    columns: [
      col.seq(),
      /*
        The advance itself, not its number typed out again.

        A free-text reference could not stop the same advance being settled
        twice or being settled for more than it was worth, and the
        `employee_advances` report's balance had no link to reconcile against.
        `advance_over_settled` can now check both.
      */
      { key: 'againstDocumentId', label: 'Advance', type: 'text', width: 220, editable: true,
        lookup: { entity: 'expense_report' } },
      col.desc(),
      col.amount('Amount used'),
    ],
    sources: [],
  }],
  columns: [
    ...baseColumns(null, 'Claimed'),
    { key: 'expenseType', label: 'Kind', type: 'text', width: 160 },
  ],
  filters: baseFilters(null),
  actions: [...SYNCED_BASIC_ACTIONS, 'changeTaxUnit'],
  rules: ['advance_over_settled'],
} as const)
